import React from "react";

const About: React.FC = () => {
  const stats = [
    {
      number: "9",
      label: "Engineering Keystones",
    },
    {
      number: "50+",
      label: "Projects Completed",
    },
    {
      number: "100%",
      label: "Sustainable Focus",
    },
    {
      number: "∞",
      label: "Innovation Potential",
    },
  ];

  const keystones = [
    "Research & Development",
    "Farm Machinery & Food Processing",
    "Energy Engineering",
    "Boats & Yachts Building",
    "Wood Works Engineering",
    "Plumbing Engineering",
    "Sustainable Building",
    "TVET/Skills Training",
    "Software & Machine Learning"
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white py-24">
        <div className="absolute inset-0">
          <img
            src="/about.jpg"
            alt="About Us"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black opacity-60"></div>
        </div>
        <div className="relative container mx-auto px-4">
          <h1 className="text-5xl font-bold mb-6">About Ekumfi Engineering</h1>
          <p className="text-lg text-gray-300 max-w-4xl">
            A comprehensive engineering conglomerate driving the industrialization of Ekumfiman through nine specialized engineering keystones. 
            We're building a self-sustainable environment that spearheads population-driven development.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Image */}
            <div className="relative">
              <video
                src="https://kasoa-market-s3.s3.us-east-1.amazonaws.com/0_Factory_Industrial_1280x720.mp4"
                autoPlay
                muted
                loop
                className="rounded-lg shadow-lg"
              />
              <div className="absolute -bottom-6 -right-6 bg-orange-500 text-white p-6 rounded-lg">
                <p className="text-4xl font-bold">9</p>
                <p className="text-sm">Engineering Pillars</p>
              </div>
            </div>

            {/* Right Column - Content */}
            <div>
              <h2 className="text-4xl font-bold mb-6">
                Industrializing Ekumfiman Through Engineering Excellence
              </h2>
              <p className="text-gray-600 mb-6">
                Ekumfi Engineering is not just a company—it's a movement towards sustainable industrialization. 
                Through our nine specialized engineering keystones, we're creating a comprehensive ecosystem that 
                addresses every aspect of community development, from basic infrastructure to cutting-edge technology.
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <svg
                    className="w-6 h-6 text-orange-500 mr-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-gray-700">
                    Comprehensive engineering solutions across 9 specialized areas
                  </span>
                </div>
                <div className="flex items-center">
                  <svg
                    className="w-6 h-6 text-orange-500 mr-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-gray-700">
                    Sustainable development and environmental consciousness
                  </span>
                </div>
                <div className="flex items-center">
                  <svg
                    className="w-6 h-6 text-orange-500 mr-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-gray-700">
                    Community empowerment and skills development
                  </span>
                </div>
                <div className="flex items-center">
                  <svg
                    className="w-6 h-6 text-orange-500 mr-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-gray-700">
                    Innovation-driven approach to industrial development
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Engineering Keystones */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Nine Engineering Keystones</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Each keystone represents a specialized area of engineering excellence, working together to create 
              a comprehensive industrial ecosystem for Ekumfiman.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {keystones.map((keystone, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-4">
                    {index + 1}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800">{keystone}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-gray-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-orange-500 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Mission */}
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="text-orange-500 mb-4">
                <svg
                  className="w-12 h-12"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-4">Our Mission</h3>
              <p className="text-gray-600">
                <strong>To industrialize Ekumfiman through engineering excellence.</strong> We are committed to creating 
                innovative engineering solutions that drive sustainable development, empower communities, and build 
                the infrastructure needed for population-driven growth.
              </p>
            </div>

            {/* Vision */}
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="text-orange-500 mb-4">
                <svg
                  className="w-12 h-12"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-4">Our Vision</h3>
              <p className="text-gray-600">
                <strong>To create a unique self-sustainable environment that spearheads population-driven development.</strong> 
                We envision Ekumfiman as a model of sustainable industrialization, where engineering innovation 
                drives economic growth and community prosperity.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-16 bg-orange-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-8">Our Impact on Ekumfiman</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">Economic Growth</h3>
              <p className="text-orange-100">
                Creating jobs, building infrastructure, and driving industrial development across all sectors.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-4">Community Empowerment</h3>
              <p className="text-orange-100">
                Training local talent, building skills, and creating opportunities for sustainable livelihoods.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-4">Environmental Sustainability</h3>
              <p className="text-orange-100">
                Implementing green technologies and sustainable practices for a better future.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
